import * as React from "react";
import { Tag, TagType } from "../types/Tagging.types";
import { ITag, Label, TagPicker as OUITagPicker, TagItem, TagItemSuggestion, TooltipHost } from "office-ui-fabric-react";
import { editTag, searchTags } from "../api/tagging";
import { NewTagDialog } from "./NewTagDialog";

export interface ITagPickerProps {
    /** Label for the picker */
    label: string;
    selectedTags?: Tag[];
    /** Type of tags to be used */
    type?: TagType;
    onChange?: (tags: Tag[]) => void;
    /** Behaviour that should occur when a tag is removed. If a Tag[] is returned, those tags will be considered not removed and kept in selected items. */
    onTagsRemoved?: (tags: Tag[]) => Tag[] | PromiseLike<Tag[]> | undefined | void;
}

export const TagPicker = (props: ITagPickerProps) => {

    const { onChange, onTagsRemoved, selectedTags: initialTags, type } = props; 
    
    // Tracks currently selected tags in the picker
    const [ selectedTags, setSelectedTags ] = React.useState<Tag[]>([]);
    const [ ready, setReady ] = React.useState(false);
    
    // setNewTag effectively opens a new tag dialog box. Set to undefined to hide.
    const [ newTag, setNewTag ] = React.useState<Tag|undefined>();

    // When selected tags is updated, notify outside components
    React.useEffect(() => {
        if (ready && onChange) {
            onChange(selectedTags);
        }
    }, [onChange, selectedTags]);

    // Reset state of New Tag dialog and hide it
    const closeNewTagForm = React.useCallback(() => {
        setNewTag(undefined);
    }, []);

    // Save a new tag and add it to the selected tags
    const saveNewTag = React.useCallback(async (tag: Tag) => {
        if (newTag) {
            const result = await editTag(tag);
            setSelectedTags([...selectedTags, result]);
        }
    }, [newTag, selectedTags]);

    // Perform a search against the database for existing tags
    const search = React.useCallback(async (filter: string) => {
        const results = await searchTags(filter, props.type);
        return results;
    }, [props?.type]);

    // TAG PICKER - Get Suggestions
    const onResolveSuggestions = React.useCallback(async (filter: string, selectedItems?: ITag[] | undefined): Promise<ITag[]> => {
        // Get search results from DB and map to OUI ITag object
        const results = await search(filter);
        let tags = results?.map((r) => ({
            key: r.Id,
            name: r.Label,
            data: r
        } as ITag)) ?? [];

        // Exclude any tags that were already picked
        if (selectedItems) {
            tags = tags.filter(t => selectedItems.map(si => si.key).indexOf(t.key) < 0);
        }

        // If this exact string is not found in DB, offer to add it
        if (tags.length === 0 || (!tags.map(t => t.name).includes(filter) && !selectedItems?.map(si => si.name).includes(filter))) {
            return [...tags, {
                key: -1,
                name: `> Click to add '${filter}'`,
                data: {
                    Id: -1,
                    Label: filter, 
                } as any as Tag
            }] as any as ITag[];
        }
        return tags;
    }, [search]);

    // TAG PICKER - OnChange
    // When the tag picker updates with a change...
    const pickerOnChange = React.useCallback((items: ITag[] | undefined) => {
        if (ready) {
            // If any items have the id -1, the user has pressed enter on a new user-defined tag. Open new tag dialog:
            const addNewTag = items?.filter(i => i.key === -1)[0];
            if (addNewTag) {
                setNewTag((addNewTag as any).data as Tag);
            }
    
            // All other tags that were added should be added to selected tags in state
            items = items?.filter(i => i.key as number > 0);
    
            // Callback for tags that are removed. We may wish to perform additional behaviour outside of the component
            // such as deleting the tag from the database when removed etc.
            const tagsRemoved = selectedTags.filter(st => !(items || []).map(i => i.key as number).includes(st.Id));
            if (tagsRemoved.length && onTagsRemoved) {
                Promise.resolve(onTagsRemoved(tagsRemoved)).then(rtnVal => {
                    // If the callback returns a Tag[], those tags are considered to have been kept
                    if (items && rtnVal) {
                        items = [...items, ...rtnVal.map(tr => ({ key: tr.Id, name: tr.Label, data: tr } as ITag))];
                    }
                });
            }
    
            // Update state
            setSelectedTags(items?.map(i => (i as any).data as Tag) || []);
        }
    }, [selectedTags, onTagsRemoved]);

    React.useEffect(() => {
        if (!ready) {
            setSelectedTags(initialTags || []);
            setReady(true);
        }
    }, [initialTags, ready, selectedTags]);

    return (
        <>
            <Label>{props.label}</Label>
            <OUITagPicker 
                disabled={!ready}
                selectedItems={selectedTags.map(st => ({
                    key: st.Id,
                    name: st.Label,
                    data: st
                } as ITag))}
                onChange={pickerOnChange}
                resolveDelay={800}
                pickerSuggestionsProps={{
                    suggestionsHeaderText: "Suggested Tags"
                }}
                onRenderItem={(itemProps) => {
                    return (
                        <TooltipHost key={itemProps.item.key} content={((itemProps.item as any).data as Tag)?.Tooltip || undefined}>
                            <TagItem {...itemProps}>{itemProps.item.name}</TagItem>
                        </TooltipHost>
                    );
                }}
                onRenderSuggestionsItem={(tagProps, suggestionItemProps) => {
                    return (
                        <TooltipHost content={tagProps.key === -1 ? 'Click here to add' : undefined}>
                            <TagItemSuggestion onClick={suggestionItemProps.onClick}>{tagProps.name}</TagItemSuggestion>
                        </TooltipHost>
                    );
                }}
                onResolveSuggestions={onResolveSuggestions} 
            />
            <NewTagDialog
                onDismiss={closeNewTagForm}
                onSubmit={saveNewTag}
                tag={newTag}
                type={type}
            />
        </>
    )
}